// Draft store of the explicit save (docs/explicit-save-plan.md §3.2).
//
// The operations of a page no longer write to the backend: they write a
// draft. The draft is a journal per scope (e.g. "properties:<id>") of the
// rows they changed, created or removed; one SAVE sends the whole journal in
// one PUT /items (one commit), Discard puts the stored rows back.
//
// The journal holds the truth (the original row, the draft row, the version
// the draft is based on). MemoryDB is written through, so every reader of the
// memory DB (useGetMemItem, useSelectMem, refManager, the tree, the tabs) sees
// the draft with no change.
//
// ***************************************************************************
// RULE: NEVER JOURNAL THE ROW OF AN OPEN ITEM FORM.
// An Item form (Formik, enableReinitialize) is initialised from its MemoryDB
// row: writing that row reinitialises the form, which loses what the user
// typed and its dirty state. The fields of the open form are changed with
// setFieldValue and saved by the form's Save (which sends the draft of its
// draftScope in the same request). The Item registers its row
// (registerOpenForm): edit/create/remove/change of that row throw, except a
// change that closes that form (its release is the change's closing).
// While the form has changes, a colleague's save of its row is held out of
// MemoryDB (THE HOLD, below): the form is not reinitialised either.
// ***************************************************************************
//
// An item is journaled in one scope at a time: an edit from another scope is
// refused (an error with code "HELD" and the scope that holds it), unless the
// caller asks for the holder explicitly ({ anyScope: true }).
//
// Pure factory (db and refManager are injected) so it can be tested; the app
// instance is in ./instance.js.

import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";

export const draftKey = (accessor, id) => `${accessor}:${id}`;

// main changes only through the release of a branch: no draft is made there
// (same wording family as the guard of the writes, useData/remote.js).
export const MAIN_DRAFT_REFUSED =
  "Changes cannot be made on main: switch to a branch";

const versionOf = (row) => Number(row?.version ?? 0);

// Same content, whatever refManager computed on the rows (refsComputed): a
// row taken from the server has none.
const sameRow = (a, b) => {
  if (!a || !b) return a === b;
  const { refsComputed: ra, ...restA } = a;
  const { refsComputed: rb, ...restB } = b;
  return isEqual(restA, restB);
};

// The content of a stored row, as two copies of it can be compared: without
// what the client computes (refsComputed), what the server owns and changes
// without a new version (refs.belongs, lastSaved), and with the top-level
// flags as the full load stores them (1/0, see booleanToIndex).
const contentOf = (row) => {
  const content = {};
  for (const [key, value] of Object.entries(row ?? {})) {
    if (key === "refsComputed" || key === "lastSaved") continue;
    if (key === "refs" && value && typeof value === "object") {
      const { belongs, ...refs } = value;
      if (Object.keys(refs).length) content.refs = refs;
      continue;
    }
    content[key] = value === true ? 1 : value === false ? 0 : value;
  }
  return content;
};
const sameContent = (a, b) => isEqual(contentOf(a), contentOf(b));

const EMPTY = Object.freeze([]);

// The data a created row is saved with when it inherits fields from another
// row (the inherit of its create op; collect gives it on the item:
// { accessor, id, apply, base, source }): apply(a copy of its draft row,
// the row it inherits from, as the batch saves it). base: the draft row
// (the row as the batch would send it without the fields inherited). With
// no such row, the draft row as it is.
export function inherited({ apply, base }, source) {
  if (!source) return cloneDeep(base);
  const row = cloneDeep(base);
  return apply(row, cloneDeep(source)) ?? row;
}

export const HELD = "HELD";
const heldError = (key, scope) => {
  const error = new Error(
    `draft: ${key} has unsaved changes in ${scope}: save or discard them there first`
  );
  error.code = HELD;
  error.scope = scope;
  return error;
};

export function createDraftStore({ db, refManager }) {
  // scope -> { entries: Map<key, entry>, conflicts: Map<key, conflict>,
  //            conflictsList }
  const scopes = new Map();
  // scope -> Map<key, rev>: the entries the last collect of the scope sent
  // (clear). Not in the scope: a scope is forgotten when its last entry
  // leaves, which can happen while the request runs.
  const collectedOf = new Map();
  // key -> { scope, original, rev }: an entry the running save sent, taken
  // out of the journal meanwhile by an edit back to its stored row
  // (original), at revision rev. The response of that save drafts the
  // stored row again on the saved one (incoming): the user's undo is kept.
  const undone = new Map();
  // key -> scope that journals it
  const owners = new Map();
  // key -> Set of the open forms on that row ({ accessor, id, key, scope,
  // stash, isDirty, released })
  const openForms = new Map();
  // key -> the hold of the row of the open forms (see registerOpenForm):
  // { accessor, id, base, conflict, pending, view }. base: the row the
  // forms were loaded from (MemoryDB's, a copy; null when there is none);
  // conflict: the server row kept out of MemoryDB while a form is dirty
  // (the shape of the conflicts of a scope); pending: { row } (row null: a
  // delete) applied when the forms settle, without a conflict (the same row
  // with other refs.belongs; a delete our own save made); view: the
  // formConflictOf object, kept until the hold changes.
  const holds = new Map();
  // key -> what we know of our own saves of the row (see OUR OWN SAVES):
  // { accessor, id, saved, expected }. saved: { version, row } or absent;
  // expected: [{ version, row, rev }].
  const ownSaves = new Map();
  // Changed by bind to another tenant or branch: the response of a request
  // sent before (revsOf tags its sent with it) is of the data left
  let generation = 0;
  // The scopes whose save runs: from the collect of the save to its clear
  // (saved) or saveSettled (not saved)
  const saving = new Set();
  // Every open form, in the order they were registered (stashOpenForms)
  const registry = new Set();
  // release -> its form: a change names the form it closes by its release
  const releases = new WeakMap();
  // scope prefix (before the first ":") -> (rest, pathname) => boolean
  const routeRules = new Map();
  const listeners = new Set();
  // Told when an operation is refused (a draft on main): the app notifies it
  const refusedListeners = new Set();
  let binding = null;
  let branch = null;
  let anyDirty = false;
  // The revision of an entry, for the whole store: a change of an entry
  // gives it a revision no entry ever had. A save compares the revision of
  // an entry when the request left with the one it has when the response
  // comes (incoming, clear): an entry dropped and drafted again meanwhile
  // (back to the stored row, then changed again) must not look unchanged.
  let revision = 0;
  const nextRev = () => ++revision;

  // *************************************************************************
  // NOTIFICATIONS
  // *************************************************************************
  const emit = () => {
    anyDirty = [...scopes.values()].some((s) => s.entries.size > 0);
    listeners.forEach((listener) => listener());
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  // listener(message): an operation was refused and changed nothing.
  const subscribeRefused = (listener) => {
    refusedListeners.add(listener);
    return () => refusedListeners.delete(listener);
  };

  // *************************************************************************
  // SCOPES
  // *************************************************************************
  const getScope = (scope, create = false) => {
    let s = scopes.get(scope);
    if (!s && create) {
      s = {
        entries: new Map(),
        conflicts: new Map(),
        conflictsList: EMPTY
      };
      scopes.set(scope, s);
    }
    return s;
  };

  // A scope with nothing in it is forgotten (no empty scope is dirty).
  const settleScope = (scope) => {
    const s = scopes.get(scope);
    if (!s) return;
    // A new array only when the conflicts changed (useSyncExternalStore)
    const list = s.conflictsList;
    if (
      list.length !== s.conflicts.size ||
      list.some((c) => s.conflicts.get(draftKey(c.accessor, c.id)) !== c)
    )
      s.conflictsList = s.conflicts.size ? [...s.conflicts.values()] : EMPTY;
    if (s.entries.size === 0 && s.conflicts.size === 0) scopes.delete(scope);
  };

  // A row that leaves the journal (undone, saved, reloaded, discarded) is no
  // dep of the rows of any scope any more (a change with anyScope records
  // deps across scopes): nothing of it is left to orphan or dangle. A row
  // drafted again later is a new one, with no partners. (A dep on a row
  // that is never journaled, the open form's, stays.)
  const pruneDeps = (key) => {
    for (const s of scopes.values()) {
      for (const entry of s.entries.values()) {
        if (!entry.deps?.includes(key)) continue;
        const deps = entry.deps.filter((dep) => dep !== key);
        if (deps.length) entry.deps = deps;
        else delete entry.deps;
      }
    }
  };

  const dropEntry = (scope, key) => {
    const s = scopes.get(scope);
    if (!s) return;
    s.entries.delete(key);
    s.conflicts.delete(key);
    if (owners.get(key) === scope) owners.delete(key);
    pruneDeps(key);
    settleScope(scope);
  };

  // *************************************************************************
  // MEMORY DB WRITE-THROUGH (as refreshData does)
  // *************************************************************************

  // The base of the open forms on a row follows what the store writes to
  // it (a discard, a reload, the hold applied): the forms are loaded from it
  const syncBase = (accessor, id, row) => {
    const hold = holds.get(draftKey(accessor, id));
    if (!hold) return;
    // formConflictOf gives the version of the base
    if (versionOf(hold.base) !== versionOf(row)) hold.view = null;
    hold.base = row ? cloneDeep(row) : null;
  };

  const writeRows = (accessor, rows) => {
    if (!rows.length) return;
    for (const row of rows) {
      db.put(accessor, row);
      syncBase(accessor, row.id, row);
    }
    refManager.fillBelongs(db.getDB(), [rows], accessor);
  };

  // A delete changes the refs of other rows (e.g. the elements of the list
  // of a smart container, the blocks nested in a page): all are recomputed
  // (refManager.refill), as a full load would.
  const deleteRows = (accessor, ids) => {
    if (!ids.length) return;
    for (const id of ids) {
      refManager.delete(accessor, id);
      db.delete(accessor, id);
      syncBase(accessor, id, null);
    }
    refManager.refill();
  };

  // rows: [{ accessor, id, row }] (row null: delete). Grouped per table: one
  // fillBelongs per table written; one refill for all the deletes and the
  // rows put back (not in MemoryDB before, e.g. a discarded delete), which
  // change the refs of other rows as a delete does.
  const writeMany = (changes) => {
    const puts = new Map();
    const deletes = [];
    let putBack = false;

    for (const { accessor, id, row } of changes) {
      if (row) {
        if (!puts.has(accessor)) puts.set(accessor, []);
        puts.get(accessor).push(cloneDeep(row));
        if (db.peek(accessor, id) === undefined) putBack = true;
      } else deletes.push({ accessor, id });
    }

    for (const [accessor, rows] of puts.entries()) {
      for (const row of rows) {
        db.put(accessor, row);
        syncBase(accessor, row.id, row);
      }
      refManager.fillBelongs(db.getDB(), [rows], accessor);
    }

    for (const { accessor, id } of deletes) {
      refManager.delete(accessor, id);
      db.delete(accessor, id);
      syncBase(accessor, id, null);
    }

    if (deletes.length || putBack) refManager.refill();
  };

  // *************************************************************************
  // GUARDS
  // *************************************************************************

  // On main nothing is drafted (it could never be saved): the operation is a
  // no-op, the app notifies why. Not bound yet: not main.
  const refusedOnMain = () => {
    if (branch !== "main") return false;
    refusedListeners.forEach((listener) => listener(MAIN_DRAFT_REFUSED));
    return true;
  };

  const onMain = () => branch === "main";

  // On main an edit that would change something is refused whatever the row
  // (open form, held, missing): prepare() throwing is a refusal too, without
  // the error. An edit that changes nothing is not refused (e.g. a
  // normalisation run when a page opens).
  const prepareOnMain = (prepare) => {
    try {
      return prepare();
    } catch (error) {
      refusedOnMain();
      return null;
    }
  };

  // closingForm: the form a change closes (the change's closing is its
  // release): its row can be drafted when no other form is open on it.
  const assertNotOpen = (accessor, id, closingForm) => {
    const forms = openForms.get(draftKey(accessor, id));
    if (!forms?.size) return;
    if (closingForm && forms.size === 1 && forms.has(closingForm)) return;
    throw new Error(
      `draft: ${accessor} ${id} is the row of an open Item form. Change it with setFieldValue: writing its MemoryDB row reinitialises the form.`
    );
  };

  // The dirty scope that journals the key, or null.
  const holderOf = (key) => {
    const owner = owners.get(key);
    return owner && scopes.get(owner)?.entries.has(key) ? owner : null;
  };

  // The scope the operation goes to: the one asked for. A row journaled in
  // another scope is refused (HELD), unless anyScope (it goes to the holder).
  const ownerFor = (key, scope, anyScope = false) => {
    const holder = holderOf(key);
    if (holder && holder !== scope) {
      if (!anyScope) throw heldError(key, holder);
      return holder;
    }
    return scope;
  };

  const journal = (scope, key, entry) => {
    getScope(scope, true).entries.set(key, entry);
    owners.set(key, scope);
    // Drafted again: the entry is the user's last state (incoming rebases it)
    undone.delete(key);
  };

  // An edit brought the entry back to its stored row while a save that
  // sent it runs (collect): remembered for the response of that save. Not
  // a row changed with others in a structural operation (deps): drafting
  // it again alone, without its partners (a created row removed has no
  // stored row to go back to), would leave them orphan or dangling.
  const recordUndone = (scope, key, entry) => {
    if (entry.deps?.length || !entry.original) return;
    if (!collectedOf.get(scope)?.has(key)) return;
    undone.set(key, {
      scope,
      original: cloneDeep(entry.original),
      rev: entry.rev
    });
  };

  // *************************************************************************
  // OPERATIONS
  // *************************************************************************

  // Everything an edit needs, computed without changing anything: an updater
  // that throws, a row held by another scope, an open form or a missing row
  // leave the draft as it was. noop: the updater changed nothing.
  const prepareEdit = (scope, accessor, id, updater, anyScope, closingForm) => {
    assertNotOpen(accessor, id, closingForm);
    const key = draftKey(accessor, id);
    const owner = ownerFor(key, scope, anyScope);
    const entry = getScope(owner)?.entries.get(key);

    if (entry?.deleted) {
      throw new Error(
        `draft: ${key} was removed in ${owner}, it can't be edited`
      );
    }

    const row = entry ? cloneDeep(entry.current) : db.get(accessor, id);
    if (row === undefined) throw new Error(`draft: ${key} not found`);
    const original = entry ? null : cloneDeep(row);
    const current = entry ? entry.current : original;
    const next = cloneDeep(updater(row) ?? row);

    return {
      kind: "edit",
      key,
      owner,
      entry,
      accessor,
      id,
      original,
      next,
      noop: sameRow(next, current)
    };
  };

  // deps: the keys of the rows changed in one operation with this one
  // (change), added to the ones the entry has.
  const addDeps = (entry, deps) => {
    if (!deps?.length) return;
    entry.deps = [...new Set([...(entry.deps ?? []), ...deps])];
  };

  // The journal side of an edit (the caller writes MemoryDB).
  const journalEdit = (
    { key, owner, entry, accessor, id, original, next },
    deps
  ) => {
    if (!entry) {
      entry = {
        accessor,
        id,
        original,
        baseVersion: versionOf(original),
        isNew: false,
        deleted: false,
        prevId: id,
        current: null,
        rev: 0
      };
      journal(owner, key, entry);
    }

    entry.current = cloneDeep(next);
    entry.rev = nextRev();
    addDeps(entry, deps);

    if (!entry.isNew && sameRow(entry.current, entry.original)) {
      recordUndone(owner, key, entry);
      dropEntry(owner, key);
    } else settleScope(owner);
  };

  const commitEdit = (prepared) => {
    journalEdit(prepared);
    writeRows(prepared.accessor, [cloneDeep(prepared.next)]);
  };

  // updater(row) => newRow: it gets a copy of the current row (the draft one
  // when the row is already journaled) and may change it in place. Returns
  // the draft row (false on main, where nothing is drafted). An edit that
  // brings the row back to the stored one takes it out of the journal; an
  // edit that changes nothing writes nothing (and is not refused on main).
  // options.anyScope: a row held by another scope is edited there.
  function edit(scope, accessor, id, updater, { anyScope = false } = {}) {
    const prepared = onMain()
      ? prepareOnMain(() => prepareEdit(scope, accessor, id, updater, anyScope))
      : prepareEdit(scope, accessor, id, updater, anyScope);
    if (!prepared) return false;
    if (prepared.noop) return cloneDeep(prepared.next);
    if (refusedOnMain()) return false;

    commitEdit(prepared);
    emit();

    return cloneDeep(prepared.next);
  }

  // Several rows in one operation, all or none: edits [{ accessor, id,
  // updater(row, prior) }] on distinct rows, prior = the new rows of the
  // edits before it. Nothing changes if one of them can't be done (throws:
  // held, open form, missing row, updater error). Returns the new rows, or
  // false on main.
  function editAll(scope, edits, { anyScope = false } = {}) {
    const prepared = [];
    const rows = [];

    for (const { accessor, id, updater } of edits) {
      const prior = rows.map((row) => cloneDeep(row));
      const prepare = () =>
        prepareEdit(
          scope,
          accessor,
          id,
          (row) => updater(row, prior),
          anyScope
        );
      const one = onMain() ? prepareOnMain(prepare) : prepare();
      if (!one) return false;
      prepared.push(one);
      rows.push(one.next);
    }

    if (prepared.every((one) => one.noop)) return rows.map(cloneDeep);
    if (refusedOnMain()) return false;

    prepared.filter((one) => !one.noop).forEach(commitEdit);
    emit();

    return rows.map(cloneDeep);
  }

  // A row that is not stored yet (row.id is its id). True once drafted (false
  // on main).
  function create(scope, accessor, row, { anyScope = false } = {}) {
    if (refusedOnMain()) return false;
    const { id } = row;
    assertNotOpen(accessor, id);
    const key = draftKey(accessor, id);
    const owner = ownerFor(key, scope, anyScope);
    const entry = getScope(owner)?.entries.get(key);

    if (entry) {
      // Created again after a remove, or replaced
      entry.deleted = false;
      entry.current = cloneDeep(row);
      entry.rev = nextRev();
    } else {
      if (db.get(accessor, id) !== undefined) {
        throw new Error(`draft: ${key} exists already, use edit`);
      }
      journal(owner, key, {
        accessor,
        id,
        original: null,
        baseVersion: 0,
        isNew: true,
        deleted: false,
        prevId: id,
        current: cloneDeep(row),
        rev: nextRev()
      });
    }

    settleScope(owner);
    writeRows(accessor, [cloneDeep(row)]);
    emit();
    return true;
  }

  // reference: the header DELETE /item wants for some types (e.g. the
  // endpoint reference). True once drafted (false on main or when the row
  // does not exist).
  function remove(scope, accessor, id, { reference, anyScope = false } = {}) {
    if (refusedOnMain()) return false;
    assertNotOpen(accessor, id);
    const key = draftKey(accessor, id);
    const owner = ownerFor(key, scope, anyScope);
    const entry = getScope(owner)?.entries.get(key);

    if (entry?.isNew) {
      // Created and removed in the same draft: nothing to save
      dropEntry(owner, key);
    } else if (entry) {
      entry.deleted = true;
      entry.current = null;
      entry.reference = reference ?? entry.reference;
      entry.rev = nextRev();
      settleScope(owner);
    } else {
      const row = db.get(accessor, id);
      if (row === undefined) return false;
      journal(owner, key, {
        accessor,
        id,
        original: cloneDeep(row),
        baseVersion: versionOf(row),
        isNew: false,
        deleted: true,
        prevId: id,
        current: null,
        reference,
        rev: nextRev()
      });
      settleScope(owner);
    }

    deleteRows(accessor, [id]);
    emit();
    return true;
  }

  // *************************************************************************
  // CHANGE: THE ROWS OF ONE OPERATION, CREATED, EDITED AND REMOVED TOGETHER
  // *************************************************************************

  const KINDS = new Set(["create", "edit", "remove"]);

  // The draft key of an op; throws on an op that is not one.
  const keyOfOp = (op) => {
    if (!KINDS.has(op?.kind))
      throw new Error(`draft: unknown change op ${JSON.stringify(op?.kind)}`);
    if (!op.accessor) throw new Error(`draft: ${op.kind} without an accessor`);
    const id = op.kind === "create" ? op.row?.id : op.id;
    if (id === undefined || id === null || id === "")
      throw new Error(`draft: ${op.kind} of ${op.accessor} without an id`);
    if (op.deps !== undefined && !Array.isArray(op.deps))
      throw new Error(`draft: the deps of ${op.kind} ${op.accessor} ${id}`);
    if (
      op.inherit !== undefined &&
      (op.kind !== "create" ||
        !op.inherit?.accessor ||
        op.inherit.id === undefined ||
        typeof op.inherit.apply !== "function")
    )
      throw new Error(`draft: the inherit of ${op.kind} ${op.accessor} ${id}`);
    return draftKey(op.accessor, id);
  };

  // A row not in MemoryDB, nor created in the journal. A stored row removed
  // earlier in the draft is put back (an edit again).
  const prepareCreate = (
    scope,
    accessor,
    row,
    anyScope,
    closingForm,
    inherit
  ) => {
    const { id } = row;
    assertNotOpen(accessor, id, closingForm);
    const key = draftKey(accessor, id);
    const owner = ownerFor(key, scope, anyScope);
    const entry = getScope(owner)?.entries.get(key);

    if (entry ? !entry.deleted : db.peek(accessor, id) !== undefined) {
      throw new Error(`draft: ${key} exists already, use edit`);
    }

    return {
      kind: "create",
      key,
      owner,
      entry,
      accessor,
      id,
      next: cloneDeep(row),
      inherit,
      noop: false
    };
  };

  // A row in MemoryDB (a row removed in the draft already: nothing to do).
  // A missing row throws (remove returns false, which change keeps for main).
  const prepareRemove = (
    scope,
    accessor,
    id,
    reference,
    anyScope,
    closingForm
  ) => {
    assertNotOpen(accessor, id, closingForm);
    const key = draftKey(accessor, id);
    const owner = ownerFor(key, scope, anyScope);
    const entry = getScope(owner)?.entries.get(key);
    const prepared = { kind: "remove", key, owner, entry, accessor, id };

    if (entry?.deleted) return { ...prepared, next: null, noop: true };

    const original = entry ? null : db.get(accessor, id);
    if (!entry && original === undefined)
      throw new Error(`draft: ${key} not found`);

    return { ...prepared, reference, original, next: null, noop: false };
  };

  // The journal side of the ops (the caller writes MemoryDB). Each returns
  // the row to write ({ accessor, id, row }, row null: delete).
  const journalCreate = (
    { key, owner, entry, accessor, id, next, inherit },
    deps
  ) => {
    if (entry) {
      // A stored row removed earlier, put back: back to the stored row, the
      // entry leaves the journal
      entry.deleted = false;
      entry.current = cloneDeep(next);
      entry.rev = nextRev();
      addDeps(entry, deps);
      if (sameRow(entry.current, entry.original)) dropEntry(owner, key);
      else settleScope(owner);
    } else {
      const created = {
        accessor,
        id,
        original: null,
        baseVersion: 0,
        isNew: true,
        deleted: false,
        prevId: id,
        current: cloneDeep(next),
        rev: nextRev()
      };
      if (inherit) created.inherit = { ...inherit };
      addDeps(created, deps);
      journal(owner, key, created);
      settleScope(owner);
    }
    return { accessor, id, row: next };
  };

  const journalRemove = (
    { key, owner, entry, accessor, id, reference, original },
    deps
  ) => {
    if (entry?.isNew) {
      // Created and removed in the same draft: nothing to save
      dropEntry(owner, key);
    } else if (entry) {
      entry.deleted = true;
      entry.current = null;
      entry.reference = reference ?? entry.reference;
      entry.rev = nextRev();
      addDeps(entry, deps);
      settleScope(owner);
    } else {
      const removed = {
        accessor,
        id,
        original,
        baseVersion: versionOf(original),
        isNew: false,
        deleted: true,
        prevId: id,
        current: null,
        reference,
        rev: nextRev()
      };
      addDeps(removed, deps);
      journal(owner, key, removed);
      settleScope(owner);
    }
    return { accessor, id, row: null };
  };

  // The rows of one operation, all or none. ops (one per key, a key
  // repeated throws):
  //   { kind: "create", accessor, row }        row.id required
  //   { kind: "edit", accessor, id, updater }  updater(row, prior), prior:
  //                                            the rows of the ops before
  //                                            (null for a remove)
  //   { kind: "remove", accessor, id, reference }
  // An op may add deps: [key], rows it depends on that are not ops of the
  // change (e.g. the property whose tree is in an open form).
  // A create may add inherit: { accessor, id, apply(row, source) => row }:
  //   fields the created row takes from another row (a new endpoint, from
  //   its property) when it is saved, not when it is created: collect gives
  //   apply(the draft row, that row as the batch saves it).
  // Every op is prepared before any is committed: a refusal of one refuses
  // the change (as edit/editAll: false on main, HELD, the open form row, a
  // create of an existing row, an edit or remove of a missing row) and
  // nothing changes. Then: commit all, closing(), one writeMany, one emit.
  // Returns Map key -> row (null for a remove), or false on main.
  // closing: the release of the form the change closes (its row can be
  //   drafted when no other form is open on it); called once at commit.
  // dryRun: prepare only (the same refusals), nothing written.
  // deps: when the change creates or removes a row, each entry records the
  // keys of the other ops (deps): the rows refer to each other (the endpoint
  // lists the new variant, the tree has its node; the endpoint no longer
  // lists the variant removed). Reloading one of them alone would leave an
  // orphan or a dangling row: it discards the whole scope instead
  // (dependentsOf). A change of edits only records none.
  function change(
    scope,
    ops,
    { anyScope = false, closing, dryRun = false } = {}
  ) {
    const keys = ops.map(keyOfOp);
    const seen = new Set();
    for (const key of keys) {
      if (seen.has(key))
        throw new Error(`draft: ${key} is in the same change twice`);
      seen.add(key);
    }

    const closingForm = closing ? releases.get(closing) : undefined;

    // An error says which op it refused (useScopeChange tells the user)
    const prepareOp = (op, rows) => {
      try {
        const { kind, accessor } = op;
        if (kind === "create")
          return prepareCreate(
            scope,
            accessor,
            op.row,
            anyScope,
            closingForm,
            op.inherit
          );
        if (kind === "remove")
          return prepareRemove(
            scope,
            accessor,
            op.id,
            op.reference,
            anyScope,
            closingForm
          );

        const prior = rows.map((row) => cloneDeep(row));
        return prepareEdit(
          scope,
          accessor,
          op.id,
          (row) => op.updater(row, prior),
          anyScope,
          closingForm
        );
      } catch (error) {
        if (error && typeof error === "object") {
          error.accessor ??= op.accessor;
          error.id ??= op.kind === "create" ? op.row?.id : op.id;
        }
        throw error;
      }
    };

    const prepareAll = () => {
      const prepared = [];
      const rows = [];
      for (const op of ops) {
        const one = prepareOp(op, rows);
        prepared.push(one);
        rows.push(one.next);
      }
      return prepared;
    };

    let prepared;
    if (onMain()) {
      // Only edits that change nothing are not refused (as editAll)
      if (ops.some((op) => op.kind !== "edit")) {
        refusedOnMain();
        return false;
      }
      prepared = prepareOnMain(prepareAll);
      if (!prepared) return false;
      if (!prepared.every((one) => one.noop)) {
        refusedOnMain();
        return false;
      }
    } else prepared = prepareAll();

    const rows = new Map(
      prepared.map((one) => [one.key, one.next && cloneDeep(one.next)])
    );
    if (dryRun) return rows;

    // The rows of a structural operation (a row created or removed) depend
    // on each other: each entry records the keys of the others (deps)
    const structural = ops.some((op) => op.kind !== "edit");
    const depsOf = (one, op) =>
      [
        ...new Set([...(structural ? keys : []), ...(op.deps ?? [])])
      ].filter((key) => key !== one.key);

    const changes = [];
    for (const [index, one] of prepared.entries()) {
      if (one.noop) continue;
      const deps = depsOf(one, ops[index]);
      if (one.kind === "create") changes.push(journalCreate(one, deps));
      else if (one.kind === "remove") changes.push(journalRemove(one, deps));
      else {
        journalEdit(one, deps);
        changes.push({ accessor: one.accessor, id: one.id, row: one.next });
      }
    }
    // A row the change took out of the journal (back to the stored row, a
    // created row removed) is no dep of the others, also of the ops
    // committed after it, which recorded it
    for (const one of prepared)
      if (!scopes.get(one.owner)?.entries.has(one.key))
        pruneDeps(one.key);

    try {
      closing?.();
    } finally {
      if (changes.length) {
        // The release of the form closed may have given its journaled row
        // what the form held (the refs.belongs of a hold): the rows as the
        // journal has them now
        writeMany(closing ? changes.map(journaledRow) : changes);
        emit();
      }
    }

    return rows;
  }

  // A change of change(), with the row its entry has now (when journaled)
  const journaledRow = (one) => {
    if (!one.row) return one;
    const key = draftKey(one.accessor, one.id);
    const scope = holderOf(key);
    const entry = scope && scopes.get(scope).entries.get(key);
    return entry?.current ? { ...one, row: entry.current } : one;
  };

  // The rows that bring the scope back to the stored ones (the originals, or
  // the server rows of its live conflicts; created rows removed). The scope
  // is forgotten, and its rows are no deps of the other scopes.
  const discardChanges = (scope) => {
    const s = scopes.get(scope);
    if (!s) return [];

    const changes = [];
    for (const [key, entry] of s.entries.entries()) {
      const conflict = s.conflicts.get(key);
      const row = conflict ? conflict.serverData : entry.original;
      changes.push({ accessor: entry.accessor, id: entry.id, row });
      if (owners.get(key) === scope) owners.delete(key);
    }

    scopes.delete(scope);
    for (const key of s.entries.keys()) pruneDeps(key);
    return changes;
  };

  // Back to the stored rows: the originals, or the server rows of the live
  // conflicts (newer than the originals); the created rows are removed.
  function discard(scope) {
    if (!scopes.has(scope)) return;

    writeMany(discardChanges(scope));
    emit();
  }

  // The rows (any scope) that a reload of some of the keys can't leave
  // behind, as they were changed in one structural operation with them
  // (deps, see change): [{ scope, accessor, id }]. Reloading one of the keys
  // discards the whole scopes of these rows.
  // - a row whose deps have one of the keys (e.g. the new variant, the
  //   variant removed, the property: their endpoint is reloaded);
  // - a row of the keys whose deps have a row still drafted in its own
  //   scope (its own scope is discarded whole).
  function dependentsOf(keys = []) {
    const wanted = new Set(keys);
    const found = [];
    if (!wanted.size) return found;

    for (const [scope, s] of scopes.entries()) {
      for (const [key, entry] of s.entries.entries()) {
        const deps = entry.deps ?? EMPTY;
        const needs = wanted.has(key)
          ? deps.some((dep) => holderOf(dep) === scope)
          : deps.some((dep) => wanted.has(dep));
        if (needs) found.push({ scope, accessor: entry.accessor, id: entry.id });
      }
    }
    return found;
  }

  // The live conflicts of the scope (or the given keys) are given up: their
  // entries leave the journal and the stored server rows are applied. When a
  // row was changed with one of them in a structural operation (deps,
  // dependentsOf), its whole scope is discarded instead: no orphan row (a
  // block no endpoint lists) nor dangling one (a tree node of a block that
  // is not there, an endpoint listing a block deleted) is left.
  // Returns { discarded: [scope] }, the scopes discarded whole.
  function reloadConflicts(scope, keys) {
    const s = scopes.get(scope);
    if (!s) return { discarded: [] };

    const reloaded = [...s.conflicts.keys()].filter(
      (key) => !keys || keys.includes(key)
    );
    const discarded = [
      ...new Set(dependentsOf(reloaded).map((dependent) => dependent.scope))
    ];

    const changes = [];
    for (const whole of discarded) changes.push(...discardChanges(whole));

    if (!discarded.includes(scope)) {
      for (const key of reloaded) {
        const conflict = s.conflicts.get(key);
        changes.push({
          accessor: conflict.accessor,
          id: conflict.id,
          row: conflict.serverData
        });
        s.entries.delete(key);
        s.conflicts.delete(key);
        if (owners.get(key) === scope) owners.delete(key);
        pruneDeps(key);
      }
      settleScope(scope);
    }

    writeMany(changes);
    emit();
    return { discarded };
  }

  // "Apply my changes on their version": the live conflicts of the scope
  // (of keys, draft keys, or all) whose changes do not overlap are based on
  // the server row. merge({ base, theirs, mine, accessor }) => { ok, row,
  // both } (diff.js; copies are given): ok with no path changed on both
  // sides, the entry takes theirs as its original (baseVersion: theirs'),
  // the merged row as its draft row (with the version, lastSaved and
  // belongs of theirs), its conflict goes and MemoryDB shows it; back to
  // theirs, it leaves the journal. Refused, the entry is left as it is:
  // reason "main", "saving" (the save of the scope runs), "none" (no live
  // conflict of the scope), "form" (the conflict held by an open form: the
  // form rebases itself, reloadHeld), "created" (a row the draft created),
  // "deleted" (deleted on either side), "both" (with both: the paths
  // changed on both sides), "error" (merge threw, with error).
  // Returns { rebased: [key], refused: [{ key, reason }] }.
  function rebaseConflicts(scope, keys, merge) {
    const s = scopes.get(scope);
    const rebased = [];
    const refused = [];
    const changes = [];
    const wanted = keys ?? [...(s?.conflicts.keys() ?? [])];

    for (const key of new Set(wanted)) {
      const refuse = (reason, more) => refused.push({ key, reason, ...more });
      if (onMain()) {
        refuse("main");
        continue;
      }
      if (saving.has(scope)) {
        refuse("saving");
        continue;
      }
      const entry = s?.entries.get(key);
      const conflict = s?.conflicts.get(key);
      if (!entry || !conflict) {
        refuse(holds.get(key)?.conflict && !holderOf(key) ? "form" : "none");
        continue;
      }
      if (entry.isNew) {
        refuse("created");
        continue;
      }
      if (
        entry.deleted ||
        conflict.action === "delete" ||
        !conflict.serverData
      ) {
        refuse("deleted");
        continue;
      }

      let result;
      try {
        result = merge({
          base: cloneDeep(entry.original),
          theirs: cloneDeep(conflict.serverData),
          mine: cloneDeep(entry.current),
          accessor: entry.accessor
        });
      } catch (error) {
        refuse("error", { error });
        continue;
      }
      if (!result?.ok || result.both?.length || !result.row) {
        refuse("both", { both: result?.both ?? [] });
        continue;
      }

      const theirs = conflict.serverData;
      const current = cloneDeep(result.row);
      if (theirs.version === undefined) delete current.version;
      else current.version = theirs.version;
      if (theirs.lastSaved) current.lastSaved = cloneDeep(theirs.lastSaved);
      entry.original = cloneDeep(theirs);
      entry.baseVersion = versionOf(theirs);
      entry.current = current;
      takeBelongs(entry, theirs);
      entry.rev = nextRev();
      s.conflicts.delete(key);
      rebased.push(key);

      if (sameRow(entry.current, entry.original)) {
        dropEntry(scope, key);
        changes.push({ accessor: entry.accessor, id: entry.id, row: theirs });
      } else
        changes.push({
          accessor: entry.accessor,
          id: entry.id,
          row: entry.current
        });
    }

    if (changes.length) {
      settleScope(scope);
      writeMany(changes);
      emit();
    }
    return { rebased, refused };
  }

  // The batch payload of the scope, in the shape of useDataSaveBatch. Items
  // and deletes carry the baseVersion of the journal (0 for the created
  // ones: the server refuses them only if the row exists) and notify: false
  // (the save notifies once for the whole draft). The data is sent with the
  // version it is based on, which the server bumps.
  // The created rows come first, in the order they were created: the server
  // prepares the items in order, and a created row must be prepared before
  // the rows that require it (the endpoint of a new variant).
  // collect is the start of a save: with something to send, the scope is
  // saving (isSaving) until clear (saved) or saveSettled (not saved).
  function collect(scope) {
    const s = scopes.get(scope);
    const created = [];
    const edited = [];
    const deletes = [];
    const collected = new Map();
    collectedOf.set(scope, collected);
    if (!s) return { items: [], deletes };

    if (s.entries.size && !saving.has(scope)) {
      saving.add(scope);
      emit();
    }

    for (const [key, entry] of s.entries.entries()) {
      collected.set(key, entry.rev);
      const { accessor, id } = entry;

      if (entry.deleted) {
        deletes.push({
          accessor,
          Id: id,
          reference: entry.reference,
          baseVersion: entry.baseVersion,
          notify: false
        });
        continue;
      }

      let data = cloneDeep(entry.current);
      if (!entry.isNew && entry.original) data.version = entry.original.version;

      const item = {
        accessor,
        Id: id,
        prevId: entry.prevId,
        isNew: entry.isNew,
        baseVersion: entry.isNew ? 0 : entry.baseVersion,
        data,
        notify: false
      };

      if (entry.isNew && entry.inherit) {
        const source = inheritedFrom(scope, entry.inherit);
        item.inherit = { ...entry.inherit, base: cloneDeep(data), source };
        item.data = inherited(item.inherit, source);
      }

      (entry.isNew ? created : edited).push(item);
    }

    return { items: [...created, ...edited], deletes };
  }

  // The row a created row inherits from, as the save of the scope sends it:
  // its draft row in the scope, else the stored row (the one it is based on
  // when another scope drafts it, MemoryDB's otherwise).
  const inheritedFrom = (scope, { accessor, id }) => {
    const key = draftKey(accessor, id);
    const holder = holderOf(key);
    const entry = holder && scopes.get(holder).entries.get(key);
    if (entry && holder === scope) return cloneDeep(entry.current);
    if (entry) return cloneDeep(entry.original);
    return cloneDeep(db.peek(accessor, id));
  };

  // After a successful save: the entries sent by the last collect leave the
  // journal (with their conflicts). An entry changed after the collect (while
  // the request was running) stays, also one that left the journal and came
  // back (its revision is new). Without a collect, everything goes.
  function clear(scope) {
    const collected = collectedOf.get(scope);
    collectedOf.delete(scope);
    const settled = saving.delete(scope);
    // The response is in: an undo it did not bring back is forgotten
    for (const [key, record] of [...undone.entries()])
      if (record.scope === scope) undone.delete(key);
    const s = scopes.get(scope);
    if (!s) {
      if (settled) emit();
      return;
    }

    for (const [key, entry] of [...s.entries.entries()]) {
      if (!collected || collected.get(key) === entry.rev) {
        s.entries.delete(key);
        s.conflicts.delete(key);
        if (owners.get(key) === scope) owners.delete(key);
        pruneDeps(key);
      }
    }

    settleScope(scope);
    emit();
  }

  // *************************************************************************
  // INCOMING ROWS (refreshData) AND RELOADS
  // *************************************************************************

  // A conflict: the server row of accessor:id is not the one the draft (or
  // the open form) is based on. { accessor, id, action: "update" |
  // "delete", serverVersion, lastSaved, serverData } (null for a delete).
  const conflictOf = (accessor, id, { action, data, username }) => {
    const deleted = action === "delete" || !data;
    return {
      accessor,
      id,
      action: deleted ? "delete" : "update",
      serverVersion: deleted ? null : versionOf(data),
      lastSaved:
        (!deleted && data?.lastSaved) ||
        (username ? { username, date: new Date().toISOString() } : undefined),
      serverData: deleted ? null : cloneDeep(data)
    };
  };

  const addConflict = (scope, entry, change) => {
    const s = getScope(scope, true);
    s.conflicts.set(
      draftKey(entry.accessor, entry.id),
      conflictOf(entry.accessor, entry.id, change)
    );
    settleScope(scope);
  };

  // refs.belongs is owned by the server (it ignores the client's), and the
  // cascades that change it keep the version: the draft takes it, no conflict.
  const takeBelongs = (entry, data) => {
    const belongs = data?.refs?.belongs;
    if (belongs === undefined) return;
    for (const row of [entry.original, entry.current]) {
      if (row) row.refs = { ...(row.refs ?? {}), belongs: cloneDeep(belongs) };
    }
  };

  // The stored row is now `data` (our own save): the draft is based on it.
  const rebase = (entry, data) => {
    entry.original = cloneDeep(data);
    entry.baseVersion = versionOf(data);
    entry.isNew = false;
    delete entry.inherit;
    if (entry.current) {
      entry.current.version = data.version;
      if (data.lastSaved) entry.current.lastSaved = data.lastSaved;
    }
    takeBelongs(entry, data);
  };

  // A row our own save sent (sent: its revision then), not journaled now:
  // when an edit brought it back to its stored row while the request ran
  // (undone), the saved row is not what the user left. That stored row is
  // drafted again, based on the saved one: true (the draft row stays in
  // MemoryDB). Nothing to draft (the server row is that row, or no undo
  // after the request left): false.
  const redraftUndone = (table, key, { action, data }, { colleague, sent }) => {
    const record = undone.get(key);
    const rev = sent?.get(key);
    if (!record || colleague || rev === undefined || rev === null) return false;
    // An undo before the request left is not one of this save
    if (record.rev <= rev) return false;
    undone.delete(key);
    if (action === "delete" || !data) return false;

    const current = cloneDeep(record.original);
    current.version = data.version;
    if (data.lastSaved) current.lastSaved = cloneDeep(data.lastSaved);
    if (sameContent(current, data)) return false;

    const entry = {
      accessor: table,
      id: data.id,
      original: cloneDeep(data),
      baseVersion: versionOf(data),
      isNew: false,
      deleted: false,
      prevId: data.id,
      current,
      rev: nextRev()
    };
    takeBelongs(entry, data);
    journal(record.scope, key, entry);
    settleScope(record.scope);
    writeRows(table, [cloneDeep(entry.current)]);
    emit();
    return true;
  };

  // A conflict our own save made obsolete: the server has our row now, at
  // least as new as the one the conflict recorded.
  const settledBy = (conflict, data) =>
    conflict && !(conflict.serverVersion > versionOf(data));

  // *************************************************************************
  // OUR OWN SAVES
  // *************************************************************************
  // The row a save of ours stored comes back by other ways than the
  // response of that save, which alone says it is ours (sent): a full
  // reload (the socket event update-request-volatile-metadata, after every
  // configuration change of the tenant), a fetch (reloadItems), the socket
  // (its update-data, when the socket got another id meanwhile). Judged
  // against the base of an open form, it was a colleague's save whenever
  // the base was not that row yet (the save still running, its response
  // lost): the form was held on the old row, with a banner naming the user.
  // So the store RECOGNISES our rows; it keeps, per row:
  // - saved: the row our last save of it stored (its response, or the row
  //   expected confirmed by a fetch or a full reload);
  // - expected: the rows the saves sent and not answered yet will store
  //   (what each sends, at the next version, as the server makes it, with
  //   the revision of the entry it sent: expectSaved; each dropped when the
  //   server refuses its save, unexpectSaved; kept when no answer came, as
  //   the save may have gone through; dropped when someone else's row
  //   reaches its version, or a rename takes the row's id away).
  // A server row equal to one of them (same version and content), not
  // older than the base of the forms, is ours (isOwnRow). The store never
  // decides that a server row is stale: an older row, not ours, is applied
  // or held as any other (the API must not serve a row older than its own
  // commits: backend v2.25.0).

  const nextVersion = (data) => {
    const version = Number(data?.version);
    return version ? version + 1 : 1;
  };

  const setOwn = (key, own) => {
    if (!own.saved && !own.expected?.length) ownSaves.delete(key);
    else ownSaves.set(key, own);
  };

  // At most a few saves of a row unanswered (Save clicked again after "Not
  // saved")
  const EXPECTED_MAX = 4;

  // The id a create or a rename comes from: no row of ours stays there
  const renamedFrom = (item, id) => {
    const prevId = item?.prevId;
    return typeof prevId === "string" &&
      prevId !== "" &&
      prevId !== "newItem" &&
      prevId !== id
      ? prevId
      : null;
  };

  // The rows a save sends (items: [{ accessor, Id, prevId, data }], as
  // saveDataItem and saveBatch send them): the rows it will store. sent:
  // the revisions of the entries it sends (revsOf, taken before): an entry
  // is settled by its row only at that revision (ownRevOf). A rename
  // (prevId) forgets what is known of the old id. Returns what
  // unexpectSaved takes back.
  function expectSaved(items = [], sent = null) {
    const expected = [];
    for (const item of items ?? []) {
      const id = item?.Id ?? item?.id;
      const data = item?.data;
      if (!item?.accessor || id === undefined) continue;
      const prevId = renamedFrom(item, id);
      if (prevId !== null) ownSaves.delete(draftKey(item.accessor, prevId));
      if (!data || typeof data !== "object") continue;
      let row;
      try {
        // As the server receives it
        row = JSON.parse(JSON.stringify(data));
      } catch (error) {
        continue;
      }
      row.id = id;
      row.version = nextVersion(data);
      const key = draftKey(item.accessor, id);
      const record = { version: row.version, row, rev: sent?.get(key) ?? null };
      const own = ownSaves.get(key);
      setOwn(key, {
        ...own,
        accessor: item.accessor,
        id,
        expected: [...(own?.expected ?? EMPTY), record].slice(-EXPECTED_MAX)
      });
      expected.push({ key, record });
    }
    return expected;
  }

  // The server refused the save (a conflict, an error it answered): it
  // stored nothing of what expectSaved recorded for it (the rows of other
  // saves stay)
  function unexpectSaved(expected = []) {
    for (const { key, record } of expected ?? []) {
      const own = ownSaves.get(key);
      if (!own?.expected?.includes(record)) continue;
      setOwn(key, {
        ...own,
        expected: own.expected.filter((one) => one !== record)
      });
    }
  }

  // Our row of key at its version is stored: the saved row, and the rows
  // expected up to its version are answered (a delete: nothing of ours to
  // keep)
  const recordSaved = (table, key, action, data) => {
    if (action === "delete") {
      ownSaves.delete(key);
      return;
    }
    const version = versionOf(data);
    setOwn(key, {
      accessor: table,
      id: data.id,
      saved: { version, row: cloneDeep(data) },
      expected: (ownSaves.get(key)?.expected ?? EMPTY).filter(
        (record) => record.version > version
      )
    });
  };

  const sameAs = (record, data) =>
    Boolean(record) &&
    record.version === versionOf(data) &&
    sameContent(record.row, data);

  // The record of our saves of key that data is (not older than base, never
  // back): "expected" (a save not answered: running, or its response
  // lost), "saved" (the row our last save stored), or null
  const ownMatch = (key, data, base = null) => {
    const own = ownSaves.get(key);
    if (!own || !data) return null;
    if (base && versionOf(data) < versionOf(base)) return null;
    if ((own.expected ?? EMPTY).some((record) => sameAs(record, data)))
      return "expected";
    return sameAs(own.saved, data) ? "saved" : null;
  };

  const isOwnRow = (key, data, base = null) =>
    ownMatch(key, data, base) !== null;

  // A server row of key that is not ours: a delete ends what we know of our
  // saves of it; a newer version ends the saved row (someone saved after
  // us), and the rows expected up to its version (our saves can't store
  // them any more).
  const supersede = (key, action, data) => {
    const own = ownSaves.get(key);
    if (!own) return;
    if (action === "delete" || !data) {
      ownSaves.delete(key);
      return;
    }
    const version = versionOf(data);
    setOwn(key, {
      ...own,
      saved: own.saved && version > own.saved.version ? undefined : own.saved,
      expected: (own.expected ?? EMPTY).filter(
        (record) => record.version > version
      )
    });
  };

  // A server row of key as the server serves it now (a fetch, a full
  // reload): a row expected is confirmed, it is the saved row (for the
  // recognition only); a row not ours ends what it supersedes
  const servedOwn = (key, action, data) => {
    if (action !== "delete" && ownMatch(key, data) === "expected") {
      const own = ownSaves.get(key);
      recordSaved(own.accessor, key, action, data);
    } else if (action === "delete" || !isOwnRow(key, data))
      supersede(key, action, data);
  };

  // The row of a save of ours (public: the Item's safety net tells our row
  // from a colleague's by it), not older than base when given
  const isOwnSave = (accessor, id, row, base = null) =>
    isOwnRow(draftKey(accessor, id), row, base);

  // The revision of a journaled entry that the save of ours storing data
  // sent: the response's (sent; null, not drafted when it left: not ours),
  // or, for the expected row of a save (a full reload, a fetch, the socket
  // bring it, before the response or without it), the revision that save
  // sent (expectSaved's rev): not the last collect's, which may be a later
  // save of the entry changed since (Save again after "Not saved"). Not the
  // saved row of an earlier save: the entry was drafted on it, it is its
  // base. undefined: not a save of ours.
  const ownRevOf = (scope, key, { action, data }, { colleague, sent } = {}) => {
    if (!colleague && sent?.has(key)) {
      const rev = sent.get(key);
      return rev === null ? undefined : rev;
    }
    if (action === "delete" || !data) return undefined;
    const record = (ownSaves.get(key)?.expected ?? EMPTY).find((one) =>
      sameAs(one, data)
    );
    return record?.rev ?? undefined;
  };

  // The entry key, sent at rev by the save of ours that stored data: saved
  // (or removed) by it, nothing left to draft; or edited again since, now
  // based on the saved row (back to what the save stored: pause, Save,
  // play, pause, nothing left to draft). True when it left the journal.
  const savedEntry = (scope, s, key, entry, { action, data }, rev) => {
    if (action === "delete" || rev === entry.rev) {
      dropEntry(scope, key);
      return true;
    }
    if (settledBy(s.conflicts.get(key), data)) s.conflicts.delete(key);
    rebase(entry, data);
    if (!entry.deleted && sameRow(entry.current, entry.original)) {
      dropEntry(scope, key);
      return true;
    }
    settleScope(scope);
    return false;
  };

  // A full reload: what it serves of the rows of our saves, taken before
  // the drafts and the forms put their rows back. Then, once they are
  // compared with it, servedRows(...).apply(): servedOwn of each; a row
  // missing is a delete.
  const servedRows = (tables) => {
    const served = [];
    for (const [key, own] of ownSaves) {
      if (tables && !tables.has(own.accessor)) continue;
      const stored = db.peek(own.accessor, own.id);
      served.push({
        key,
        stored: stored === undefined ? undefined : cloneDeep(stored)
      });
    }
    return {
      apply: () => {
        for (const { key, stored } of served) {
          if (stored === undefined) ownSaves.delete(key);
          else servedOwn(key, "update", stored);
        }
      }
    };
  };

  // The forms on key, one with changes, told that the row of a save of
  // ours not answered (match "expected": its response lost) goes under
  // them, their base now: the Item keeps what was typed after that save
  // (onOwnSave(row, base, { match }) of its registration)
  const tellOwnSave = (key, row, base, match) => {
    for (const form of [...(openForms.get(key) ?? [])]) {
      if (typeof form.onOwnSave !== "function") continue;
      try {
        form.onOwnSave(cloneDeep(row), base ? cloneDeep(base) : null, {
          match
        });
      } catch (error) {
        console.error("⚠️ OWN SAVE OF AN OPEN FORM", error);
      }
    }
  };

  // *************************************************************************
  // THE HOLD: THE ROW OF A DIRTY OPEN FORM
  // *************************************************************************
  // While a form open on a row that no draft journals has changes (its
  // isDirty), a server row of it (a colleague's save, a reload) is kept out
  // of MemoryDB: the form is not reinitialised (what is typed stays), and
  // MemoryDB stays on the row the form was loaded from (its base), so that
  // its Save sends the base version (rawItem.version) and is refused (409,
  // or locally: conflictsOf) instead of overwriting the colleague's row.
  // The server row is a conflict of the form (formConflictOf), applied when
  // the form is clean again (formSettled), closed (release) or reloaded
  // (reloadHeld). A row in the journal is never held: the journal has its
  // own conflicts (incoming).

  const snapshot = (row) =>
    row === undefined || row === null ? null : cloneDeep(row);

  const newHold = (accessor, id) => ({
    accessor,
    id,
    base: snapshot(db.peek(accessor, id)),
    conflict: null,
    pending: null,
    view: null
  });

  // The hold of the forms open on key, or null without a form. bind forgets
  // the holds: one is made again from MemoryDB (nothing is held then).
  const holdOf = (key) => {
    const forms = openForms.get(key);
    if (!forms?.size) return null;
    let hold = holds.get(key);
    if (!hold) {
      const [{ accessor, id }] = forms;
      hold = newHold(accessor, id);
      holds.set(key, hold);
    }
    return hold;
  };

  // A form on key has changes (one whose isDirty throws counts as dirty)
  const formsDirty = (key) => {
    for (const form of openForms.get(key) ?? []) {
      try {
        if (form.isDirty()) return true;
      } catch (error) {
        return true;
      }
    }
    return false;
  };

  // The scope of the forms on key (the first that has one), or null
  const formScope = (key) => {
    for (const form of openForms.get(key) ?? [])
      if (form.scope) return form.scope;
    return null;
  };

  // The conflict of a hold: the server row (or delete) kept out of
  // MemoryDB. own: the row is our own save's (OUR OWN SAVES: its saved row,
  // come back under a form edited on another row since, e.g. an older row
  // a stale server showed meanwhile): not a colleague's, the banner and the
  // dialog say so
  const heldConflictFrom = (hold, key, change) => {
    const conflict = conflictOf(hold.accessor, hold.id, change);
    if (change.action !== "delete" && isOwnRow(key, change.data, hold.base))
      conflict.own = true;
    return conflict;
  };

  const setHeldConflict = (hold, conflict) => {
    hold.conflict = conflict;
    hold.view = null;
  };

  // What the hold keeps goes into MemoryDB (their row, their delete, or the
  // pending row), and the forms are based on it. True when there was one.
  const applyHold = (hold) => {
    const { accessor, id, conflict, pending } = hold;
    if (!conflict && !pending) return false;
    const row = conflict ? conflict.serverData : pending.row;
    hold.conflict = null;
    hold.pending = null;
    hold.view = null;
    if (row) writeRows(accessor, [cloneDeep(row)]);
    else if (db.peek(accessor, id) !== undefined) deleteRows(accessor, [id]);
    else hold.base = null;
    if (conflict) emit();
    return true;
  };

  // The conflict held when the row of our own save arrives is newer than
  // it: a delete (our save succeeded, so it was made after it), or a
  // higher version.
  const newerThan = (conflict, data) =>
    conflict.action === "delete" || conflict.serverVersion > versionOf(data);

  // A server row of the row of open forms, not journaled. True: kept out of
  // MemoryDB (held, pending, or the newer held row written instead). False:
  // the caller applies it, and it is the base of the forms now.
  // - our own save of it (sent has it), or no form dirty: applied;
  // - the same row as the base (same version and content: a refs.belongs
  //   cascade): pending, no conflict;
  // - a delete made by our own save of another row (sent has not the key:
  //   the old id of a row renamed): pending;
  // - the row of a save of ours not answered (its response lost, or not
  //   come yet) brought by another way (a fetch, the socket: ownMatch
  //   "expected"): applied, the base now, as its response would make it,
  //   the forms told first (tellOwnSave); a newer save of a colleague held
  //   stays held. The saved row of an answered save under a form with
  //   changes on another base is held as any row (the form was based on
  //   another row since: its changes may clash with ours);
  // - otherwise (a new version, other content, a delete): the conflict of
  //   the form (the last one received).
  const holdIncoming = (hold, key, { action, data }, options) => {
    const { colleague, sent, username } = options;
    const deleted = action === "delete";
    const own = !colleague && Boolean(sent);
    const ours = own && sent.has(key);

    if (ours || !formsDirty(key)) {
      if (ours && hold.conflict && newerThan(hold.conflict, data)) {
        // A colleague saved it after our save: their row, not ours
        applyHold(hold);
        return true;
      }
      const had = hold.conflict;
      hold.base = deleted ? null : cloneDeep(data);
      hold.conflict = null;
      hold.pending = null;
      hold.view = null;
      if (had) emit();
      return false;
    }

    if (own && deleted) {
      hold.pending = { row: null };
      return true;
    }

    if (
      !deleted &&
      hold.base &&
      versionOf(data) === versionOf(hold.base) &&
      sameContent(hold.base, data)
    ) {
      hold.pending = sameRow(hold.base, data) ? null : { row: cloneDeep(data) };
      return true;
    }

    if (!deleted && ownMatch(key, data, hold.base) === "expected") {
      const had = hold.conflict;
      // The form keeps what was typed after that save (the row goes under
      // it): told before the row is written
      tellOwnSave(key, data, hold.base, "expected");
      hold.base = cloneDeep(data);
      hold.pending = null;
      hold.view = null;
      if (had && !newerThan(had, data)) hold.conflict = null;
      if (had) emit();
      return false;
    }

    setHeldConflict(
      hold,
      heldConflictFrom(hold, key, { action, data, username })
    );
    emit();
    return true;
  };

  // A row that comes from the server (refreshData, reloadItems): true when it
  // must not be applied to MemoryDB, as the row is journaled and its draft
  // stays visible.
  //
  // - A row our own write sent (sent: Map key -> rev of the entry when the
  //   request left): applied, it leaves the journal (with the conflicts it
  //   made obsolete), unless it was edited again while the request was
  //   running: the draft is then based on the saved row (and leaves the
  //   journal, the saved row applied, when it is that row).
  // - Any other row (a colleague's save, or a server-side change made by our
  //   save to a row it did not send: modifiche, a delete cascade): the draft
  //   is based on the stored row it was taken from. The same row again (same
  //   version and content: a refs.belongs cascade) gives the draft its
  //   belongs; anything else (a new version, other content saved without a
  //   new version, a delete) is a live conflict, the save of the draft will
  //   be refused.
  // A row not journaled, of open forms one of which is dirty, is held (see
  // holdIncoming): true, MemoryDB stays on the row the forms are based on.
  // The response of a request sent for another tenant or branch (bind
  // since; its sent knows the generation): of the data left, nothing of it
  // applies (true).
  // options.fetched: a fetch of the stored row (reloadItems), not an event:
  // a row of a save of ours not answered is confirmed (see OUR OWN SAVES).
  function incoming(table, { action, data }, options = {}) {
    const { sent } = options;
    if (sent?.generation !== undefined && sent.generation !== generation)
      return true;
    const kept = incomingRow(table, { action, data }, options);
    if (options.fetched && data?.id !== undefined)
      servedOwn(draftKey(table, data.id), action, data);
    return kept;
  }

  function incomingRow(table, { action, data }, options) {
    const { sent, username } = options;
    const id = data?.id;
    if (id === undefined) return false;

    const key = draftKey(table, id);
    if (!options.colleague && sent?.has(key))
      recordSaved(table, key, action, data);
    else if (action === "delete" || !isOwnRow(key, data))
      supersede(key, action, data);

    const scope = holderOf(key);
    const s = scope && scopes.get(scope);
    const entry = s?.entries.get(key);
    if (!entry) {
      const hold = holdOf(key);
      if (hold && holdIncoming(hold, key, { action, data }, options))
        return true;
      return redraftUndone(table, key, { action, data }, options);
    }

    const deleted = action === "delete";
    let keep = true;
    let touched = false;

    const rev = ownRevOf(scope, key, { action, data }, options);
    if (rev !== undefined) {
      if (savedEntry(scope, s, key, entry, { action, data }, rev)) keep = false;
      else touched = true;
    } else if (deleted) {
      if (entry.deleted) {
        // Removed on both sides: nothing left to save
        dropEntry(scope, key);
        keep = false;
      } else addConflict(scope, entry, { action, data, username });
    } else if (
      !entry.isNew &&
      versionOf(data) === entry.baseVersion &&
      sameContent(entry.original, data)
    ) {
      // The stored row the draft is based on: no conflict (any more)
      takeBelongs(entry, data);
      s.conflicts.delete(key);
      settleScope(scope);
      touched = true;
    } else addConflict(scope, entry, { action, data, username });

    if (touched && entry.current && !entry.deleted) {
      // The draft row, with what it took from the server row
      writeRows(table, [cloneDeep(entry.current)]);
    }
    // Out of the journal, the server row goes to MemoryDB (the caller): the
    // forms open on it are based on it
    if (!keep) syncBase(table, id, deleted ? null : data);

    emit();
    return keep;
  }

  // The entries the request sends, with their revision at that time. The
  // map knows the tenant and branch it was made for (generation): its
  // response after a change of them is of the data left (incoming).
  function revsOf(keys) {
    const revs = new Map();
    for (const key of keys) {
      const scope = owners.get(key);
      const entry = scope && scopes.get(scope)?.entries.get(key);
      revs.set(key, entry ? entry.rev : null);
    }
    Object.defineProperty(revs, "generation", { value: generation });
    return revs;
  }

  // After a full reload of the tables (updateData): the drafts go back into
  // MemoryDB. The stored row of an entry is compared with the one the draft
  // is based on: another version or content (or gone) is a conflict; the same
  // row clears a conflict recorded before (e.g. by a reload that failed).
  // tables: the tables reloaded (a table whose fetch failed keeps its rows,
  // the drafts in it are left as they are); all when not given.
  // The row a save of ours stored, of an entry it sent (before its
  // response, or without it), is that save's, as its response would be.
  // Then what it serves of the rows of our saves (servedRows).
  function reapply({ tables } = {}) {
    const changes = [];
    const served = servedRows(tables);

    for (const [scope, s] of [...scopes.entries()]) {
      for (const [key, entry] of [...s.entries.entries()]) {
        if (tables && !tables.has(entry.accessor)) continue;
        const stored = db.get(entry.accessor, entry.id);
        const change = { action: "update", data: stored };
        const rev =
          stored === undefined || entry.deleted
            ? undefined
            : ownRevOf(scope, key, change);
        if (rev !== undefined) {
          if (!savedEntry(scope, s, key, entry, change, rev))
            changes.push({
              accessor: entry.accessor,
              id: entry.id,
              row: entry.current
            });
          continue;
        }
        const unchanged =
          stored !== undefined &&
          !entry.isNew &&
          versionOf(stored) === entry.baseVersion &&
          sameContent(entry.original, stored);

        if (entry.deleted) {
          if (stored === undefined) {
            // Gone on the server as well
            dropEntry(scope, key);
            continue;
          }
          if (unchanged) s.conflicts.delete(key);
          else addConflict(scope, entry, { action: "update", data: stored });
          changes.push({ accessor: entry.accessor, id: entry.id, row: null });
          continue;
        }

        if (entry.isNew) {
          if (stored !== undefined)
            addConflict(scope, entry, { action: "update", data: stored });
        } else if (stored === undefined) {
          addConflict(scope, entry, { action: "delete", data: null });
        } else if (!unchanged) {
          addConflict(scope, entry, { action: "update", data: stored });
        } else {
          takeBelongs(entry, stored);
          s.conflicts.delete(key);
        }

        changes.push({
          accessor: entry.accessor,
          id: entry.id,
          row: entry.current
        });
      }
      settleScope(scope);
    }

    changes.push(...reapplyHolds(tables));
    writeMany(changes);
    served.apply();
    emit();
  }

  // The rows of the open forms after a full reload (not journaled): a
  // dirty form, or one with a conflict held, gets its base back (the
  // reload put the stored row in MemoryDB), and the stored row is compared
  // with it as incoming does (a conflict, or pending; the row of a save of
  // ours not answered, ownMatch "expected": the base now, it stays in
  // MemoryDB, a newer save of a colleague held stays held). The others take
  // the stored row as their base. Returns the rows to write.
  const reapplyHolds = (tables) => {
    const changes = [];
    for (const [key, forms] of openForms.entries()) {
      const [{ accessor, id }] = forms;
      if (tables && !tables.has(accessor)) continue;
      if (holderOf(key)) continue;

      const known = holds.has(key);
      const hold = holdOf(key);
      const stored = db.peek(accessor, id);
      // After bind (no hold), or nothing to keep: the stored row goes
      if (!known || (!hold.conflict && !formsDirty(key))) {
        hold.base = snapshot(stored);
        hold.pending = null;
        hold.view = null;
        continue;
      }

      const { base, conflict } = hold;
      if (stored === undefined) {
        if (base) {
          if (conflict?.action !== "delete")
            setHeldConflict(
              hold,
              conflictOf(accessor, id, { action: "delete" })
            );
        } else if (conflict) setHeldConflict(hold, null);
      } else if (
        !base ||
        versionOf(stored) !== versionOf(base) ||
        !sameContent(base, stored)
      ) {
        if (ownMatch(key, stored, base) === "expected") {
          if (formsDirty(key)) tellOwnSave(key, stored, base, "expected");
          hold.base = snapshot(stored);
          hold.pending = null;
          hold.view = null;
          if (conflict && !newerThan(conflict, stored)) hold.conflict = null;
          continue;
        }
        // The same server row as the conflict held keeps it (its who/when)
        if (
          !(
            conflict?.action === "update" &&
            conflict.serverVersion === versionOf(stored) &&
            sameContent(conflict.serverData, stored)
          )
        )
          setHeldConflict(
            hold,
            heldConflictFrom(hold, key, { action: "update", data: stored })
          );
      } else {
        // Found again as the base: no conflict (any more)
        if (conflict) setHeldConflict(hold, null);
        hold.pending = sameRow(base, stored)
          ? null
          : { row: cloneDeep(stored) };
      }

      if (base) {
        if (stored === undefined || !sameRow(base, stored))
          changes.push({ accessor, id, row: base });
      } else if (stored !== undefined)
        changes.push({ accessor, id, row: null });
    }
    return changes;
  };

  // The data loaded is the one of tenant/branch: the drafts of another
  // tenant or branch are dropped (their rows are not loaded any more), and
  // so are the holds of the open forms (the next load is their base) and
  // what is known of our own saves. On main no draft is made.
  // True when the tenant or the branch changed (the drafts were dropped).
  function bind(tenant, nextBranch) {
    const next = `${tenant}|${nextBranch}`;
    const changed = binding !== null && binding !== next;
    if (changed) {
      scopes.clear();
      owners.clear();
      collectedOf.clear();
      undone.clear();
      holds.clear();
      ownSaves.clear();
      generation += 1;
      saving.clear();
      emit();
    }
    binding = next;
    branch = nextBranch;
    return changed;
  }

  // *************************************************************************
  // OPEN FORMS AND ROUTES
  // *************************************************************************

  // The row of an open form (THE RULE). Returns release (idempotent: a form
  // released by its own stash is released again when it unmounts).
  // scope, stash: a form that can put its changes into the draft of scope
  // before a navigation that stays in it (stashOpenForms). stash(destination)
  // => "none" | "stashed" | "invalid" | "refused", synchronously; it stashes
  // with change(scope, [...], { closing: release }).
  // isDirty() => boolean: the form has changes (default: always, as a form
  // that registers only while it has them, useOpenFormWhile). While one is
  // dirty, a server row of the row is held (see THE HOLD); the row of
  // MemoryDB now is the base of the forms on it.
  // onOwnSave(row, base, { match }): the row of a save of ours not answered
  // (match "expected": its response lost, see OUR OWN SAVES) is let through
  // as the base while a form has changes (the user typed after that save):
  // called before it is written, so the form can keep what was typed (the
  // Item sets its values on the row).
  function registerOpenForm(
    accessor,
    id,
    { scope = null, stash = null, isDirty = null, onOwnSave = null } = {}
  ) {
    const key = draftKey(accessor, id);
    const form = {
      accessor,
      id,
      key,
      scope,
      stash,
      isDirty: typeof isDirty === "function" ? isDirty : () => true,
      onOwnSave,
      released: false
    };
    if (!openForms.has(key)) openForms.set(key, new Set());
    openForms.get(key).add(form);
    registry.add(form);
    if (!holds.has(key)) holds.set(key, newHold(accessor, id));
    else formsChanged(key);

    const release = () => {
      if (form.released) return;
      form.released = true;
      registry.delete(form);
      const forms = openForms.get(key);
      forms?.delete(form);
      if (forms?.size === 0) {
        openForms.delete(key);
        releaseHold(key);
      } else formsChanged(key);
    };
    releases.set(release, form);
    return release;
  }

  // Another form on the row, or one less: its conflict may have another
  // scope; with no form dirty left, what the hold keeps is applied.
  const formsChanged = (key) => {
    const hold = holds.get(key);
    if (!hold || (!hold.conflict && !hold.pending)) return;
    if (!holderOf(key) && !formsDirty(key)) {
      applyHold(hold);
      return;
    }
    if (hold.conflict) {
      hold.view = null;
      emit();
    }
  };

  // The last form on the row is gone: the hold ends.
  // - The row is journaled now (the stash of the form: a change with its
  //   release as closing): the journal takes the conflict held, in the
  //   scope's conflicts (the entry is based on the base: it was drafted
  //   from MemoryDB), and the belongs pending.
  // - Otherwise what it keeps goes into MemoryDB (their row, their delete,
  //   the pending row).
  const releaseHold = (key) => {
    const hold = holds.get(key);
    holds.delete(key);
    if (!hold || (!hold.conflict && !hold.pending)) return;

    const scope = holderOf(key);
    if (!scope) {
      applyHold(hold);
      return;
    }

    const s = scopes.get(scope);
    const entry = s.entries.get(key);
    const { conflict, pending } = hold;
    if (pending?.row) takeBelongs(entry, pending.row);
    if (conflict) s.conflicts.set(key, conflict);
    else if (pending && !pending.row)
      s.conflicts.set(
        key,
        conflictOf(hold.accessor, hold.id, { action: "delete" })
      );
    settleScope(scope);
    emit();
  };

  // The form on accessor:id is clean again (the Item, when its dirty turns
  // false): with no form dirty on it, what the hold keeps is applied (their
  // row, their delete, the pending row), so a clean form shows the stored
  // row (a Reset with a conflict held reloads it). True when something was.
  function formSettled(accessor, id) {
    const key = draftKey(accessor, id);
    const hold = holds.get(key);
    if (!hold || holderOf(key) || formsDirty(key)) return false;
    return applyHold(hold);
  }

  // "Reload theirs" of a form: what the hold keeps is applied, whatever the
  // form (it is reset, or rebased, on it); the forms are based on it now.
  // True when something was held.
  function reloadHeld(accessor, id) {
    const key = draftKey(accessor, id);
    const hold = holds.get(key);
    if (!hold || holderOf(key)) return false;
    return applyHold(hold);
  }

  // Worst last
  const STASH_OUTCOMES = ["none", "stashed", "invalid", "refused"];

  // Before a navigation to destination: every open form whose scope stays on
  // it (as scopesLeftBy: "" stays, null leaves) puts its changes into the
  // draft (its stash). A stash that throws is "refused", an unknown outcome
  // "invalid". { outcome: the worst (refused > invalid > stashed > none),
  // forms: [{ accessor, id, scope, outcome, error? }] }.
  function stashOpenForms(destination) {
    const forms = [];

    // A stash releases its form: the registry changes while it is read
    for (const form of [...registry]) {
      if (form.released || !form.scope || typeof form.stash !== "function")
        continue;
      if (!staysOnDestination(form.scope, destination)) continue;

      let outcome;
      let error;
      try {
        outcome = form.stash(destination);
      } catch (e) {
        outcome = "refused";
        error = e;
      }
      if (!STASH_OUTCOMES.includes(outcome)) outcome = "invalid";

      forms.push({
        accessor: form.accessor,
        id: form.id,
        scope: form.scope,
        outcome,
        ...(error !== undefined && { error })
      });
    }

    const outcome = forms.reduce(
      (worst, form) =>
        STASH_OUTCOMES.indexOf(form.outcome) > STASH_OUTCOMES.indexOf(worst)
          ? form.outcome
          : worst,
      "none"
    );
    return { outcome, forms };
  }

  // The pages that stay inside the scopes of a prefix: navigating among them
  // keeps the draft (e.g. a property and its variant pages). Without a rule,
  // any navigation leaves the scope.
  function registerScopeRoutes(prefix, staysOn) {
    routeRules.set(prefix, staysOn);
  }

  const staysOn = (scope, pathname) => {
    const at = scope.indexOf(":");
    const prefix = at === -1 ? scope : scope.slice(0, at);
    const rest = at === -1 ? "" : scope.slice(at + 1);
    const rule = routeRules.get(prefix);
    return rule ? Boolean(rule(rest, pathname)) : false;
  };

  // A navigation to destination stays in the scope: never with destination
  // null (every scope is left), always with "" (no navigation, e.g. a tab of
  // the page).
  const staysOnDestination = (scope, destination) => {
    if (destination === null || destination === undefined) return false;
    if (destination === "") return true;
    return staysOn(scope, String(destination).split(/[?#]/)[0]);
  };

  // The dirty scopes a navigation leaves: all of them with destination null,
  // none with "" (no navigation, e.g. a tab of the page).
  function scopesLeftBy(destination) {
    return scopesDirty().filter(
      (scope) => !staysOnDestination(scope, destination)
    );
  }

  // *************************************************************************
  // READ
  // *************************************************************************

  // The live conflicts of the given rows (draft keys, e.g. the rows a write
  // sends), whatever their scope, in the shape of the conflicts of a refused
  // save (409): { type, id, baseVersion, storedVersion, lastSaved }, plus
  // accessor, scope and action ("delete": the row is gone on the server, and
  // saving it would create it again). The conflict held by an open form
  // (THE HOLD) too, with form: true (scope: the form's, or null).
  function conflictsOf(keys = []) {
    const found = [];
    for (const key of new Set(keys)) {
      const scope = owners.get(key);
      const s = scope && scopes.get(scope);
      const conflict = s?.conflicts.get(key);
      if (!conflict) {
        const held = heldConflictOf(key);
        if (held) found.push({ type: held.accessor, ...held, form: true });
        continue;
      }

      found.push({
        type: conflict.accessor,
        accessor: conflict.accessor,
        id: conflict.id,
        scope,
        action: conflict.action,
        baseVersion: s.entries.get(key)?.baseVersion,
        storedVersion: conflict.serverVersion,
        lastSaved: conflict.lastSaved
      });
    }
    return found;
  }

  // The rows of keys (draft keys) on which a form is open (registerOpenForm,
  // whatever its scope or dirty): e.g. a delete that would take a row from
  // under its form asks first.
  function openFormsOn(keys = []) {
    return [...new Set(keys)].filter((key) => openForms.get(key)?.size > 0);
  }

  // The conflict held for the forms open on key (not journaled), as
  // formConflictOf gives it: kept until the hold changes.
  const heldConflictOf = (key) => {
    const hold = holds.get(key);
    if (!hold?.conflict || holderOf(key)) return null;
    if (!hold.view) {
      const { accessor, id, base, conflict } = hold;
      hold.view = Object.freeze({
        accessor,
        id,
        scope: formScope(key),
        action: conflict.action,
        baseVersion: versionOf(base),
        storedVersion: conflict.serverVersion,
        lastSaved: conflict.lastSaved,
        ...(conflict.own && { own: true })
      });
    }
    return hold.view;
  };

  // The conflict of the open form on accessor:id (a server row held while
  // it is dirty): { accessor, id, scope, action, baseVersion,
  // storedVersion, lastSaved, own? }, or null (own: the row held is our own
  // save, see heldConflictFrom). The same object until it changes
  // (useSyncExternalStore).
  const formConflictOf = (accessor, id) =>
    heldConflictOf(draftKey(accessor, id));

  // The rows of the conflict of accessor:id, held by an open form or live
  // in a draft (copies), or null: { base, theirs } (base: the row the form
  // or the draft is based on; theirs: the server row, null for a delete).
  // A draft's also gives mine: its draft row (null for a delete).
  function conflictVersions(accessor, id) {
    const key = draftKey(accessor, id);
    const scope = holderOf(key);
    if (scope) {
      const s = scopes.get(scope);
      const conflict = s.conflicts.get(key);
      if (!conflict) return null;
      const entry = s.entries.get(key);
      return {
        base: snapshot(entry.original),
        theirs: snapshot(conflict.serverData),
        mine: snapshot(entry.current)
      };
    }
    const hold = holds.get(key);
    if (!hold?.conflict) return null;
    return {
      base: snapshot(hold.base),
      theirs: snapshot(hold.conflict.serverData)
    };
  }

  // The dirty scope that holds accessor:id (null when not drafted).
  const ownerOf = (accessor, id) => holderOf(draftKey(accessor, id));

  // The stored row a drafted accessor:id is based on (null for a row the
  // draft created); undefined when the row is not drafted.
  function storedOriginal(accessor, id) {
    const key = draftKey(accessor, id);
    const scope = holderOf(key);
    if (!scope) return undefined;
    const original = scopes.get(scope).entries.get(key).original;
    return original ? cloneDeep(original) : null;
  }

  // The id, version and lastSaved of the stored original of a journaled
  // accessor:id, with no copy (read at render, e.g. a version pin): null
  // when the draft created it, undefined when it is not drafted.
  function storedStampOf(accessor, id) {
    const key = draftKey(accessor, id);
    const scope = holderOf(key);
    if (!scope) return undefined;
    const original = scopes.get(scope).entries.get(key).original;
    if (!original) return null;
    const { version, lastSaved } = original;
    return { id: original.id, version, lastSaved };
  }

  // The draft row of a journaled accessor:id (a copy), or null when it is
  // removed in the draft or not drafted (e.g. "mine" of a conflict).
  function currentOf(accessor, id) {
    const key = draftKey(accessor, id);
    const scope = holderOf(key);
    const current = scope && scopes.get(scope).entries.get(key).current;
    return current ? cloneDeep(current) : null;
  }

  const count = (scope) => scopes.get(scope)?.entries.size ?? 0;
  const conflicts = (scope) => scopes.get(scope)?.conflictsList ?? EMPTY;
  const isDirty = (scope) => count(scope) > 0;
  function scopesDirty() {
    return [...scopes.entries()]
      .filter(([, s]) => s.entries.size > 0)
      .map(([scope]) => scope);
  }
  const isAnyDirty = () => anyDirty;

  // The save of the scope runs (from its collect): Discard, Reload, Rebase
  // and the Item's Reset wait for it.
  const isSaving = (scope) => saving.has(scope);

  // The save of the scope ended without saving (refused, failed, a
  // conflict): the scope is not saving any more. A save that succeeded
  // ends with clear.
  function saveSettled(scope) {
    if (saving.delete(scope)) emit();
  }

  // For tests and debugging
  const entries = (scope) => [...(scopes.get(scope)?.entries.values() ?? [])];

  return {
    edit,
    editAll,
    create,
    remove,
    change,
    discard,
    reloadConflicts,
    rebaseConflicts,
    dependentsOf,
    collect,
    clear,
    saveSettled,
    incoming,
    revsOf,
    expectSaved,
    unexpectSaved,
    isOwnSave,
    reapply,
    bind,
    registerOpenForm,
    formSettled,
    reloadHeld,
    stashOpenForms,
    registerScopeRoutes,
    scopesLeftBy,
    ownerOf,
    storedOriginal,
    storedStampOf,
    currentOf,
    count,
    conflicts,
    conflictsOf,
    openFormsOn,
    formConflictOf,
    conflictVersions,
    isDirty,
    isSaving,
    scopesDirty,
    isAnyDirty,
    entries,
    subscribe,
    subscribeRefused
  };
}
